import Link from 'next/link';
import htmlParser, { domToReact } from 'html-react-parser';
import DOMPurify from 'isomorphic-dompurify';
import { InstagramEmbed } from 'react-social-media-embed';
import Ad from '../components/Ad/Ad';
import { AdSizes } from '../components/Ad/AdSizes';
import CorusImage from '../components/CorusImage/CorusImage';
import VideoPlayerShortcodeResolver from '../components/video/VideoPlayer/VideoPlayerShortcodeResolver';
import WayIn from '../components/WayIn/WayIn';
import {
	isValidURL,
	RemoveEmptyTags,
	replaceLinkWithNextLink,
	tryOrDefault,
	uuidRegex,
} from './utility';
import { SCRIPTED_IFRAME_CONFIG } from '../components/Text/HtmlWrapper';
import {
	hasAriaLabel,
	hasChildren,
	hasImgChild,
	hasVideoPlayerChild,
	hasInlineStyles,
	hasLinkChild,
	hasScriptChild,
	isIframe,
	isImage,
	isLinkWithHref,
	isParagraph,
	isScript,
	nodeIs,
} from './htmlParserPredicates';

/**
 * Trusted sources for embed scripts.
 * 
 * The embed script's source URL MUST exactly match this URL.
 * @type {Array<string>}
 */
export const TRUSTED_EMBED_SCRIPT_SOURCES = [
	'https://www.instagram.com/embed.js',
	'//www.instagram.com/embed.js',
	'https://www.tiktok.com/embed.js',
	'https://platform.twitter.com/widgets.js',
];

/**
 * Host URLs for some embeds.
 */
export const TRUSTED_EMBED_SCRIPT_HOSTS = {
	videoPlayerHost: 'https://videoplayer.smdg.ca',
	wayInHost: 'https://xd.wayin.com/embed',
};

/**
 * !!!         !!!
 * !!! WARNING !!!
 * !!!         !!!
 * 
 * !!! EDIT THE CODE BELOW WITH CARE - ALTERING THIS CODE MAY EXPOSE THE WEBSITE TO XSS.
 * !!! ALWAYS HAVE ANOTHER DEVELOPER REVIEW THIS CODE CAREFULLY.
 * !!! ALWAYS DEFAULT TO REMOVING THE SCRIPT NODE HERE - THIS IS THE SAFEST BET.
 * 
 * !!! IF POSSIBLE, HAVE THE SCRIPT SOURCE URLS ABOVE AGREE WITH A `script-src` LIST IN THE CONTENT SECURITY POLICY.
 * 
 * Keep script tags which meet the following criteria:
 * * Does NOT have an inline script.
 * * Has a script source listed above in `TRUSTED_EMBED_SCRIPT_SOURCES`.
 * 
 * Defaults to removing the script tag. Script tag is kept ONLY if the above conditions are met.
 */

/**
 * `uponSanitizeElement` callback for DOMPurify's hook by the same name.
 * @param {Element} node 
 * @param {import('isomorphic-dompurify').SanitizeElementHookEvent} data 
 * @returns void
 */
export const uponSanitizeElement = (node, data) => {
	if (data.tagName === 'script') {
		/**
		 * The script node. Quick alias to add this type so we have more type information.
		 * @type {HTMLScriptElement}
		 */
		const scriptNode = node;

		/** The script's inline text, if available. */
		const scriptText = scriptNode.textContent;

		/**
		 * The script's source URL, if available.
		 * @type {string | null | undefined}
		 */
		const scriptSrc = scriptNode.attributes?.src?.value;

		// Does the script have a source URL?
		const hasScriptSrc = Boolean(scriptSrc);

		// Does the script include any inline text? e.g., `<script>alert("Hello World!");</script>`
		const hasInlineScript = Boolean(scriptText);

		// Does the script's source URL start with a trusted host?
		const startsWithEmbedScript = Object.values(TRUSTED_EMBED_SCRIPT_HOSTS).some((host) => scriptSrc?.startsWith(host) ?? false);

		// Does the script's source URL match a known trusted URL OR starts with a trusted host?
		const hasTrustedScriptSrc = (
			TRUSTED_EMBED_SCRIPT_SOURCES.includes(scriptSrc) || startsWithEmbedScript
		);

		let isScriptSafe = false;

		// If the script has a source URL...
		if (hasScriptSrc) {
			// The script is safe if it has a trusted source URL
			// AND does not have an inline script.
			isScriptSafe = hasTrustedScriptSrc && !hasInlineScript;
		} else {
			// Since the script does NOT have a source URL here,
			// the script is safe if it has an inline script, as WordPress is injecting this script.

			// This check feels pretty useless, though...
			isScriptSafe = hasInlineScript;
		}

		if (isScriptSafe) {
			return; // Keep the node and do nothing.
		}

		/**
		 * Remove the script node by default, for instance if the script node contains an inline script,
		 * has an untrusted source URL, or otherwise does not meet the criteria for a safe script.
		 * 
		 * We cannot check all possible combinations of safe / unsafe scripts, so let's select ONLY the nodes
		 * we know are safe and discard everything else.
		 */
		node.remove();
	}
};

DOMPurify.addHook('uponSanitizeElement', uponSanitizeElement);
/**
 * !!!         !!!
 * !!! WARNING !!!
 * !!!         !!!
 * 
 * !!! EDIT THE CODE ABOVE WITH CARE - ALTERING THIS CODE MAY EXPOSE THE WEBSITE TO XSS.
 */

/**
 * @summary Replace a WayIn embed script with the WayIn React component.
 * @param {import('html-react-parser').DOMNode} domNode 
 */
function replaceWayInEmbed(domNode) {
	/** @type {string | null | undefined} */
	const scriptSrc = domNode?.attribs?.src;

	if (scriptSrc && scriptSrc.startsWith(TRUSTED_EMBED_SCRIPT_HOSTS.wayInHost)) {
		// Parse the URL
		const scriptUrl = tryOrDefault(() => new URL(scriptSrc), '');
		const wayInUuid = scriptUrl
			?.pathname
			// Extract the UUID using the same Regex we use to validate it.
			?.match(uuidRegex)
			// UUID is the first element in the result array.
			?.[0];

		if (wayInUuid) {
			return (
				<WayIn uuid={wayInUuid} />
			);
		}
		return (
			<></>
		);
	}

	return undefined;
}

/**
 * @summary Replace the title of any iframe element.
 * @param {import('html-react-parser').DOMNode} domNode 
 * @returns {import('html-react-parser').DOMNode}
 */
function replaceIframeTitle(domNode) {
	const newNode = domNode;

	newNode.attribs.title = 'Embedded media';

	if (domNode.attribs.class === 'instagram-media instagram-media-rendered') {
		newNode.attribs.title = 'Instagram embedded media';
	}

	return newNode;
}

/**
 * @summary Remove any inline styles on the current element.
 * @param {import('html-react-parser').DOMNode} domNode 
 * @returns {import('html-react-parser').DOMNode}
 */
function removeInlineStyles(domNode) {
	const newNode = domNode;

	newNode.attribs.style = '';

	return newNode;
}

/**
 * @summary Join the text content of the current element and its children into the ARIA label,
 * so that the ARIA label matches the text content.
 * @param {import('html-react-parser').DOMNode} domNode 
 * @returns {import('html-react-parser').DOMNode}
 */
function joinTextContentIntoAriaLabel(domNode) {
	const newNode = domNode;

	newNode.attribs['aria-label'] = '';

	for (let i = 0; i < domNode.children.length; i += 1) {
		const currentElement = domNode.children[i];

		if (currentElement.type === 'text') {
			newNode.attribs['aria-label'] += currentElement.data;
		}

		if (currentElement.children) {
			for (let j = 0; j < currentElement.children.length; j += 1) {
				newNode.attribs['aria-label'] += currentElement.children[j].data;
			}
		}
	}

	return newNode;
}

/**
 * Parse the incoming html and convert it into React components.
 * Pass imageSizes array for parsing the html images and convert them to nextjs ones.
 * @param {string | null | undefined} post String of raw HTML representing the post.
 * @param {{ htmlId?: number, wrapperClass?: string, className?: string} | null | undefined} adsAttribs Attributes for any ads to be embedded in this posts.
 * @returns {import('react').ReactNode}
 */

export default function htmlToReact(post, adsAttribs = null) {
	if (!post?.content || post.content === '') {
		return null;
	}

	let index = 0;
	let wordsCounter = 0;
	let adsIncrement = 0;

	/** @type {import('html-react-parser').HTMLReactParserOptions} */
	const options = {
		replace: (domNode) => {
			// Replace the image's parent <p> tags with <div> to avoid invalid html error
			if (isParagraph(domNode) && hasImgChild(domNode)) {
				return (
					<div>{domToReact(domNode.children)}</div>
				);
			}

			// Replace the image's parent <h?> tags with <div> to avoid invalid html error
			if (nodeIs(domNode, 'h1')) {
				return (
					<div className="article-heading">{domToReact(domNode.children)}</div>
				);
			}

			// Remove all inline styles from user generated content
			if (hasInlineStyles(domNode)) {
				return removeInlineStyles(domNode);
			}

			// Replace aria-label on parents with links so it matches the link text
			if (hasAriaLabel(domNode) && hasLinkChild(domNode)) {
				const newNode = domNode;
				newNode.attribs['aria-label'] = domNode.childNodes[hasLinkChild(domNode)].children[0].data;
				return newNode;
			}

			// Replace elements with aria-lables which don't match their data
			if (hasAriaLabel(domNode)) {
				return joinTextContentIntoAriaLabel(domNode);
			}

			if (isIframe(domNode)) {
				return replaceIframeTitle(domNode);
			}

			// Find all video shortcodes and render a video player item
			if (isParagraph(domNode) && hasVideoPlayerChild(domNode)) {
				return <VideoPlayerShortcodeResolver content={domNode} />;
			}

			// Find out instagram URLs, and replace them with embeds.
			if (domNode?.data && domNode?.data?.indexOf('instagram.com/') !== -1 && domNode?.type === 'text') {
				// If the URL is surrounded by other text, simply return the whole text.
				if (!isValidURL(domNode?.data)) {
					return domNode;
				}

				return (
					<div className="react-instagram-embed">
						<InstagramEmbed url={domNode?.data} width="100%" />
					</div>
				);
			}

			if (post?.__typename === 'Post'
				&& post.gallery.articleGalleryRepeater === null // Exclude galleries
				&& isParagraph(domNode) // Only include paragraphs
				&& domNode?.children[0]?.data?.indexOf('instagram.com/') === -1 // Exclude instagram embeds
				&& domNode?.attribs?.class !== 'wp-caption-text' // Exclude image captions
			) {
				// Check if it is the first p to inject the first ad.
				if (index === 0 || wordsCounter > 280) {
					index += 1;
					wordsCounter = 0;
					adsIncrement += 1;

					// Add Tabnabbing by replacing <a> without both rel="noopener noreferrer" in attributes.
					domNode.children.forEach((item) => {
						if (item?.type === 'tag'
							&& item?.attribs?.target === '_blank'
							&& Array.isArray(item?.attribs?.rel)
							&& !item?.attribs?.rel.includes('noopener noreferrer')
						) {
							// eslint-disable-next-line no-param-reassign
							item.attribs.rel += ' noreferrer';
						}
					});

					return (
						<>
							<p>{domToReact(domNode.children)}</p>
							<Ad
								sizes={{
									'xs sm': AdSizes.BIG_BOX_COMBO,
									md: AdSizes.LEADERBOARD_BIGBOX_COMBO,
									lg: AdSizes.BIG_BOX_COMBO,
									'xl xxl': AdSizes.LEADERBOARD_BIGBOX_COMBO,
								}}
								htmlId={adsAttribs?.htmlId + adsIncrement}
								wrapperClass={adsAttribs?.wrapperClass}
								className={adsAttribs?.className}
								post={post}
								pos={`${adsIncrement + 1}`} // Position var starts at 2
							/>
						</>
					);
				}
				// Add the number of words per each paragraph to the main words counter.
				wordsCounter += domNode?.children[0]?.data ? domNode?.children[0]?.data?.trim().split(/\s+/).length : 0;
				index += 1;
			}

			// Add aria-label to all <a> with images
			if (isParagraph(domNode) && domNode?.children[0]?.name === 'a' && domNode?.children[0]?.children[0]?.name === 'img') {
				const tagNode = domNode?.children[0];
				const imgNode = domNode?.children[0]?.children[0];
				const altText = imgNode?.attribs?.alt;

				const { href, rel, target } = tagNode?.attribs ?? { href: '', rel: '', target: '' };
				const {
					src, alt, title, height, width,
				} = imgNode?.attribs ?? {
					src: '', alt: '', title: '', height: '', width: '',
				};

				let relAttr = 'noopener';
				if (target === '_blank' && !rel.includes('noopener noreferrer')) {
					relAttr = 'noopener noreferrer';
				}

				return (
					<p>
						<Link href={href} rel={relAttr} target={target} aria-label={altText}>

							<div className="article-content-image">
								<CorusImage
									src={src}
									alt={alt}
									title={title}
									className={imgNode.attribs?.class}
									height={parseInt(height, 10)}
									width={parseInt(width, 10)}
								/>
							</div>

						</Link>
					</p>
				);
			}

			// Replace all images with optimized next/image components
			if (isImage(domNode)) {
				return (
					<div className="article-content-image">
						<CorusImage
							src={domNode.attribs?.src}
							alt={domNode.attribs?.alt}
							title={domNode.attribs?.title}
							className={domNode.attribs?.class}
							height={parseInt(domNode.attribs?.height, 10)}
							width={parseInt(domNode.attribs?.width, 10)}
						/>
					</div>
				);
			}

			if (isLinkWithHref(domNode)) {
				return replaceLinkWithNextLink(domNode);
			}

			/**
			 * Replace WayIn embed script elements with the WayIn component
			 * to properly handle injecting the embed since it uses `document.write`.
			 */
			if (isScript(domNode)) {
				return replaceWayInEmbed(domNode);
			}

			if (hasChildren(domNode) && hasScriptChild(domNode)) {
				const scriptNode = domNode?.childNodes?.find((node) => node.name === 'script');

				return replaceWayInEmbed(scriptNode);
			}

			return domNode;
		},
	};

	const contentWithoutEmptyTags = RemoveEmptyTags(post.content);

	const sanitizedContent = DOMPurify.sanitize(contentWithoutEmptyTags, SCRIPTED_IFRAME_CONFIG.config);

	return htmlParser(sanitizedContent, options);
}
